import { AbstractControl } from '@angular/forms';
import * as _ from 'lodash';
import { InputCheckConfig } from '../interfaces/input-check-config.interface';

/**
 * Helper functions
 */
export class Helper {
	/**
	 * Capitalize first letter
	 *
	 * @param string
	 */
	public static upperFirst(string): string {
		return string[0].toUpperCase() + string.slice(1);
	}

	/**
	 * Check if control is invalid
	 */
	public static isInvalid(control: AbstractControl, config: InputCheckConfig = {}): boolean {
		// Set config
		config = Object.assign(
			{
				checkAlways: false,
				checkWhenTouched: true,
				checkWhenValueSet: true
			},
			config
		);

		// Initial state: valid = false, invalid = true
		// When disabled, then valid is always false
		// When pending, then valid and invalid are always false
		// => return true, when control is really not valid (independent from disabled): invalid or pending
		let check = false;
		if (control) {
			const invalid = control.invalid || control.pending;

			// and / or is touched
			const touched = config.checkWhenTouched && control.touched;

			// and / or value is set and never changed before
			const never = config.checkWhenValueSet && control.pristine && control.value;

			check = invalid && (config.checkAlways || touched || never);
		}

		if (!check) {
			check = false;
		}

		// workaround to avoid invalid feedback if isUniqueUsername request is pending
		if (check && !control.errors) {
			return false;
		}
		return check;
	}

	/**
	 * Check if control is valid
	 */
	public static isValid(control: AbstractControl, config?: InputCheckConfig): boolean {
		return !control ? false : !Helper.isInvalid(control, config);
	}

	/**
	 * Merge config
	 *
	 * Special merge function in which the arrays are not merged, but replaced by the source arrays.
	 *
	 * @param object Default configuration
	 * @param sources Specifications
	 * @returns
	 */
	public static mergeConfig(object: any, ...sources: any[]): any {
		// Return merged config
		return _.mergeWith(object, ...sources, (objValue: any, srcValue: any) => {
			// Arrays are not merged but replace the existing data
			if (_.isArray(srcValue)) {
				return srcValue;
			}
		});
	}

	/**
	 * Get param from URL
	 *
	 * @param name Name of the parameter
	 * @param queryString Query string
	 */
	public static getUrlParam(name: string, queryString: string = location.search) {
		try {
			return new URLSearchParams(queryString).get(name) || undefined;
		} catch (err) {
			const results = new RegExp('[?&]' + name + '=([^&#]*)').exec(queryString ? queryString : window.location.href);
			if (results === null) {
				return undefined;
			} else {
				return decodeURI(results[1]) || undefined;
			}
		}
	}

	/**
	 * Get age from date
	 * @ {number | string | Date} Date for calculation of age, can be
	 *    - Unix Time Stamp which is an integer value representing the number of milliseconds since
	 *        January 1, 1970, 00:00:00 UTC
	 *    - String value representing a date
	 *    - Date object
	 */
	public static getAge(date: number | string | Date): number {
		const today = new Date();
		const birthDate = new Date(date);
		let age = today.getFullYear() - birthDate.getFullYear();
		const m = today.getMonth() - birthDate.getMonth();
		if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
			age--;
		}
		return age;
	}

	/**
	 * Check if browser is an IE 6 - 11
	 */
	public static isBrowserIE() {
		// MSIE used to detect old browsers and Trident used to newer ones
		return navigator.userAgent.indexOf('MSIE ') > -1 || navigator.userAgent.indexOf('Trident/') > -1;
	}

	/**
	 * Get positive number
	 */
	public static getPositiveNumber(value: any, min: number = 1, max: number = null) {
		// Check value
		if (!value) {
			return min;
		}

		// Return number
		return Number(value) && Number(value) > min ? (!max || Number(value) < max ? Number(value) : max) : min;
	}
}
